import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Alert, AlertDescription } from '../ui/alert';
import { Spinner } from '../ui/spinner';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';
import { FlaskConical, Plus, Upload, Trash2, Zap } from 'lucide-react';
import { useBrand } from '../../context/BrandContext';
import translationLabAPI from '../../services/translationLabService';
import { FORMAT_EXAMPLE, countBlocks } from '../../utils/transcriptText';

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-GB', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

function defaultSetName() {
  return `Test set ${new Date().toLocaleDateString('en-GB')}`;
}

function NewSetDialog({ open, onOpenChange, onCreated }) {
  const [name, setName] = useState(defaultSetName);
  const [rawText, setRawText] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  const reset = () => {
    setName(defaultSetName());
    setRawText('');
    setError('');
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError('');
    try {
      const text = await file.text();
      setRawText(text);
      if (!name || name === defaultSetName()) setName(file.name.replace(/\.[^.]+$/, ''));
    } catch {
      setError('Could not read that file');
    } finally {
      e.target.value = ''; // allow re-picking the same file
    }
  };

  const handleCreate = async () => {
    if (!rawText.trim() || saving) return;
    setSaving(true);
    setError('');
    try {
      const { data } = await translationLabAPI.createSet({ name: name.trim() || defaultSetName(), raw_text: rawText });
      reset();
      onOpenChange(false);
      onCreated(data.set.id);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create set');
    } finally {
      setSaving(false);
    }
  };

  const blocks = countBlocks(rawText);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>New test set</DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <label className="text-xs font-bold text-ink-dim">Name</label>
            <Input value={name} onChange={(e) => setName(e.target.value)} className="mt-1" />
          </div>

          <div>
            <div className="flex items-center justify-between">
              <label className="text-xs font-bold text-ink-dim">Conversations</label>
              <Button size="sm" variant="outline" className="gap-1.5" onClick={() => fileInputRef.current?.click()}>
                <Upload className="h-3.5 w-3.5" />Upload .txt
              </Button>
              <input ref={fileInputRef} type="file" accept=".txt,text/plain" className="hidden" onChange={handleFile} />
            </div>
            <Textarea
              rows={12}
              className="mt-1 font-mono text-xs"
              placeholder={FORMAT_EXAMPLE}
              value={rawText}
              onChange={(e) => setRawText(e.target.value)}
            />
            <p className="text-[11px] text-ink-mute mt-1.5 leading-relaxed">
              A blank line separates conversations. In each conversation the <strong className="text-ink-dim">last line
              is translated and rated</strong>; the lines above it are sent as context only. Multi-line conversations
              need a <code className="text-ink-dim">user:</code> or <code className="text-ink-dim">assistant:</code> prefix
              on every line; a single-line conversation needs none.
            </p>
            {rawText.trim() && (
              <p className="text-[11px] text-ink-dim mt-1">{blocks} conversation{blocks === 1 ? '' : 's'} detected</p>
            )}
          </div>

          {error && (
            <Alert variant="error">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleCreate} disabled={!rawText.trim() || saving} className="gap-1.5">
            {saving ? <Spinner size="sm" /> : <Plus className="h-4 w-4" />}Create set
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// QuickTranslateDialog is the one-off path: a single line becomes a one-conversation set, so it
// flows through exactly the same run and rating machinery as an uploaded file.
function QuickTranslateDialog({ open, onOpenChange, onCreated }) {
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleCreate = async () => {
    const trimmed = text.trim();
    if (!trimmed || saving) return;
    setSaving(true);
    setError('');
    try {
      const { data } = await translationLabAPI.createSet({
        name: `Quick — ${trimmed.slice(0, 40)}${trimmed.length > 40 ? '…' : ''}`,
        raw_text: trimmed,
      });
      setText('');
      onOpenChange(false);
      onCreated(data.set.id);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create set');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Quick translate</DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <Textarea
            rows={3}
            placeholder="One message to translate — no conversation context"
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
          <p className="text-[11px] text-ink-mute">
            Creates a one-conversation set so you can pick an agent, re-run it with another and rate both.
          </p>
          {error && (
            <Alert variant="error">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleCreate} disabled={!text.trim() || saving} className="gap-1.5">
            {saving ? <Spinner size="sm" /> : <Zap className="h-4 w-4" />}Create
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const TranslationLabPage = () => {
  const navigate = useNavigate();
  const { activeBrand } = useBrand();
  const [sets, setSets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [newSetOpen, setNewSetOpen] = useState(false);
  const [quickOpen, setQuickOpen] = useState(false);
  // Identifies the most recent load, so responses that arrive out of order can be dropped.
  const latestLoadID = useRef(0);

  const load = useCallback(async () => {
    // Switching brands faster than a response comes back leaves two loads in flight, and the older
    // one landing last would list another brand's sets. Only the newest may write.
    const requestID = ++latestLoadID.current;
    setLoading(true);
    setError('');
    try {
      const { data } = await translationLabAPI.listSets();
      if (requestID !== latestLoadID.current) return;
      setSets(data.sets || []);
    } catch (err) {
      if (requestID !== latestLoadID.current) return;
      setError(err.response?.data?.error || 'Failed to load test sets');
    } finally {
      if (requestID === latestLoadID.current) setLoading(false);
    }
  }, []);

  // Sets are per-brand, so switching brands reloads the list.
  useEffect(() => { load(); }, [load, activeBrand]);

  const handleDelete = async (set) => {
    if (!window.confirm(`Delete "${set.name}" and all of its runs and ratings?`)) return;
    try {
      await translationLabAPI.deleteSet(set.id);
      setSets((prev) => prev.filter((s) => s.id !== set.id));
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete set');
    }
  };

  return (
    <div className="max-w-[1100px] mx-auto py-6">
      <Card className="mb-4">
        <CardContent className="p-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FlaskConical className="h-5 w-5 text-primary-500" />
            <div>
              <h2 className="text-xl font-bold">Localization</h2>
              <p className="text-xs text-ink-dim">
                Translate a fixed set of conversations, rate the results, then re-run the same set with another agent.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" className="gap-1.5" onClick={() => setQuickOpen(true)}>
              <Zap className="h-4 w-4" />Quick translate
            </Button>
            <Button className="gap-1.5" onClick={() => setNewSetOpen(true)}>
              <Plus className="h-4 w-4" />New set
            </Button>
          </div>
        </CardContent>
      </Card>

      {error && (
        <Alert variant="error" className="mb-4">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-12"><Spinner size="lg" /></div>
          ) : sets.length === 0 ? (
            <p className="text-sm text-ink-dim text-center py-12">
              No test sets yet. Upload a file of conversations to start comparing agents.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead className="w-32">Conversations</TableHead>
                  <TableHead className="w-24">Runs</TableHead>
                  <TableHead className="w-48">Created</TableHead>
                  <TableHead className="w-16" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {sets.map((set) => (
                  <TableRow
                    key={set.id}
                    className="cursor-pointer"
                    onClick={() => navigate(`/translation-lab/${set.id}`)}
                  >
                    <TableCell className="font-bold text-ink">{set.name}</TableCell>
                    <TableCell className="text-ink-dim">{set.case_count}</TableCell>
                    <TableCell className="text-ink-dim">{set.run_count}</TableCell>
                    <TableCell className="text-ink-dim">{fmtDate(set.created_at)}</TableCell>
                    <TableCell>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); handleDelete(set); }}
                            className="p-1 rounded-md text-ink-mute hover:text-negative transition-colors"
                            aria-label="Delete set"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent>Delete set</TooltipContent>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <NewSetDialog
        open={newSetOpen}
        onOpenChange={setNewSetOpen}
        onCreated={(id) => navigate(`/translation-lab/${id}`)}
      />
      <QuickTranslateDialog
        open={quickOpen}
        onOpenChange={setQuickOpen}
        onCreated={(id) => navigate(`/translation-lab/${id}`)}
      />
    </div>
  );
};

export default TranslationLabPage;
